// WHICH SPACE A NOTIFICATION IS ABOUT. The shared resolver (@paradigm/notifications)
// opens a row in a group only when the row carries `group_id`, and a row addressed
// to one person (audience 'user') never does: a proposal waiting in Book Club, a
// node someone changed there. Those carry the group in their payload
// (`group_id` with `in_group: true`, or a link that starts /group/<id>/), and open
// in that group, not in your own space. An invitation names a group too, but it
// is not IN the group (`in_group` is absent): it opens where you answer it.
//
// The payload is believed only on the platform's own rows, and only for a UUID — the
// resolver's rule (payloadGroupOf): an app writes its rows' payload freely.

import { hrefFor, payloadGroupOf } from '@paradigm/notifications';
import { mapHref } from '../pages/activity/activityData';

const GROUP_LINK = /^\/group\/([^/?#]+)(?:[/?#]|$)/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The group a row is about: its own group_id, else its payload's (when it is in
 *  the group), else its link's; null for none. */
export function groupOfRow(n) {
    if (!n) return null;
    if (n.group_id) return String(n.group_id);
    const fromPayload = payloadGroupOf(n);
    if (fromPayload) return fromPayload;
    if (n.source === 'third_party') return null;
    const p = n.payload && typeof n.payload === 'object' ? n.payload : {};
    const link = [p.link, p.url, p.href, p.path, n.link, n.url].find((x) => typeof x === 'string' && GROUP_LINK.test(x));
    if (!link) return null;
    let gid;
    try { gid = decodeURIComponent(link.match(GROUP_LINK)[1]); } catch { return null; }
    return UUID.test(gid) ? gid : null;
}

/** The row as the resolver should read it: with the group it is about. */
export function withSpace(n) {
    const g = groupOfRow(n);
    return g && !n.group_id ? { ...n, group_id: g } : n;
}

/** Where a row opens, on the map: the resolver's address, in the row's space,
 *  carried onto the page that holds it now. ONE ANSWER for every door — the
 *  Activity inbox, the dock's inbox and an OS notification. The dock skipped
 *  the rewrite, so a proposal opened Graph with ?proposal= nobody reads. */
export const destinationOf = (n, space = 'personal') => mapHref(hrefFor(withSpace(n), 'nucleus', space));

/** May this viewer dismiss the row: only their own (audience 'user'), and only from the feed. */
export const canDismiss = (n) => !!n && n.lane === 'feed' && (n.audience == null || n.audience === 'user');
